import { getSoundUrl } from "../assets";

type SoundName =
  | "shoot"
  | "explosion"
  | "hit"
  | "gameover"
  | "thrust"
  | "confirm"
  | "phaser_loop"
  | "phaser_denied";

export class AudioManager {
  private ctx: AudioContext | null = null;
  private buffers: Map<SoundName, AudioBuffer> = new Map();
  private initialized = false;
  private loading = false;

  // Phaser loop state
  private phaserLoopSource: AudioBufferSourceNode | null = null;
  private phaserLoopGain: GainNode | null = null;

  async init(): Promise<void> {
    if (this.initialized || this.loading) return;
    this.loading = true;

    try {
      this.ctx = new AudioContext();

      // Load all sounds in parallel
      const soundNames: SoundName[] = [
        "shoot",
        "explosion",
        "hit",
        "gameover",
        "thrust",
        "confirm",
        "phaser_loop",
        "phaser_denied",
      ];
      await Promise.all(soundNames.map((name) => this.loadSound(name)));

      this.initialized = true;
    } catch (e) {
      console.warn("Web Audio not supported:", e);
    } finally {
      this.loading = false;
    }
  }

  private async loadSound(name: SoundName): Promise<void> {
    if (!this.ctx) return;

    const url = getSoundUrl(name);
    if (!url) return;

    try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await this.ctx.decodeAudioData(arrayBuffer);
      this.buffers.set(name, audioBuffer);
    } catch (e) {
      console.warn(`Failed to load sound: ${name}`, e);
    }
  }

  private play(name: SoundName): void {
    if (!this.ctx || !this.initialized) return;

    const buffer = this.buffers.get(name);
    if (!buffer) return;

    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.ctx.destination);
    source.start();
  }

  playShoot(): void {
    this.play("shoot");
  }

  playExplosion(): void {
    this.play("explosion");
  }

  playHit(): void {
    this.play("hit");
  }

  playGameOver(): void {
    this.play("gameover");
  }

  playThrust(): void {
    this.play("thrust");
  }

  playConfirm(): void {
    this.play("confirm");
  }

  playPhaserDenied(): void {
    this.play("phaser_denied");
  }

  startPhaserLoop(): void {
    if (!this.ctx || !this.initialized) return;
    if (this.phaserLoopSource) return; // Already playing

    const buffer = this.buffers.get("phaser_loop");
    if (!buffer) return;

    // Create gain node for fade control
    this.phaserLoopGain = this.ctx.createGain();
    this.phaserLoopGain.connect(this.ctx.destination);
    this.phaserLoopGain.gain.setValueAtTime(0, this.ctx.currentTime);
    this.phaserLoopGain.gain.linearRampToValueAtTime(1, this.ctx.currentTime + 0.05);

    // Create looping source
    this.phaserLoopSource = this.ctx.createBufferSource();
    this.phaserLoopSource.buffer = buffer;
    this.phaserLoopSource.loop = true;
    this.phaserLoopSource.connect(this.phaserLoopGain);
    this.phaserLoopSource.start();
  }

  stopPhaserLoop(): void {
    if (!this.ctx || !this.phaserLoopSource || !this.phaserLoopGain) return;

    // Fade out quickly
    const now = this.ctx.currentTime;
    this.phaserLoopGain.gain.setValueAtTime(this.phaserLoopGain.gain.value, now);
    this.phaserLoopGain.gain.linearRampToValueAtTime(0, now + 0.05);

    // Stop after fade
    const source = this.phaserLoopSource;
    setTimeout(() => {
      try {
        source.stop();
      } catch {
        // Already stopped
      }
    }, 60);

    this.phaserLoopSource = null;
    this.phaserLoopGain = null;
  }
}

export const audioManager = new AudioManager();
